import React, { useState, useEffect, useMemo } from 'react'
import './AlertHub.css'
import { useAuth } from '../../Auth';
import PortalHeader from '../../client-dashboard/clientNavbar/PortalHeader'


// Icons
import { MdRadar, MdOutlineError } from "react-icons/md";
import { FaGlobeAmericas, FaServer, FaChartLine, FaUserCircle } from "react-icons/fa";
import { FaMicrochip } from "react-icons/fa6";
import { LuBrain } from "react-icons/lu";
import { IoInformationCircle, IoBuild } from "react-icons/io5";
import { TiWarning } from "react-icons/ti";
import { BiSolidArchive } from "react-icons/bi";

interface Alerts {
    id: number;
    category: string;
    title: string;
    body: string;
    type: string;
    created_at: any;
    api_key_name: string;
    owner: number;
    archive: boolean;
}

interface Department {
    user_id: number;
    department: string;
}

const baseDepartments = [
    { name: "Detection", icon: <MdRadar className="alert-icon" /> },
    { name: "GIS", icon: <FaGlobeAmericas className="alert-icon" /> },
    { name: "Hardware", icon: <FaMicrochip className="alert-icon" /> },
    { name: "Manufacture", icon: <IoBuild className="alert-icon" /> },
    { name: "Server", icon: <FaServer className="alert-icon" /> },
    { name: "AI", icon: <LuBrain className="alert-icon" /> },
    { name: "Marketing", icon: <FaChartLine className="alert-icon" /> },
    { name: "Account", icon: <FaUserCircle className="alert-icon" /> },
];

const getBackgroundColor = (dept: string) => {
    switch (dept) {
        case "Detection": return "#1E88E5";
        case "GIS": return "#43A047";
        case "Hardware": return "#FB8C00";
        case "Manufacture": return "#969ba9";
        case "Server": return "#8E24AA";
        case "AI": return "#3949AB";
        case "Marketing": return "#E53935";
        case "Account": return "#00897B";
        default: return "grey";
    }
}

const getAlertTypeClass = (type: string) => {
    switch (type) {
        case "info": return "alert-info";
        case "warning": return "alert-warning";
        case "error": return "alert-error";
        default: return "";
    }
}

const AlertHub = () => {
    const [allAlerts, setAllAlerts] = useState<Alerts[]>([]);
    const [departmentsPerUser, setDepartmentsPerUser] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [showArchived, setShowArchived] = useState(false);
    const [isLoading, setIsLoading] = useState(false);      // initial/fresh load
    const [isLoadingMore, setIsLoadingMore] = useState(false); // load more only
    const [showSkeleton, setShowSkeleton] = useState(false);
    const [errorCounts, setErrorCounts] = useState<Record<string, number>>({});
    const [hasFetched, setHasFetched] = useState(false);

    // 🔎 Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [alertType, setAlertType] = useState("");

    const { department: userDepartment } = useAuth();

    // Pagination
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const departments = useMemo(() => {
        if (!userDepartment) return baseDepartments;
        const userDeptObj = baseDepartments.find(d => d.name === userDepartment);
        if (!userDeptObj) return baseDepartments;
        return [userDeptObj, ...baseDepartments.filter(d => d.name !== userDepartment)];
    }, [userDepartment]);

    useEffect(() => {
        if (userDepartment) setSelectedDepartment(userDepartment);
    }, [userDepartment]);

    // Fetch departments once
    useEffect(() => {
        const fetchDepartments = async () => {
            const res = await fetch('/portal/api/department_view/', { credentials: "same-origin" });
            const data = await res.json();
            if (res.ok) setDepartmentsPerUser(data);
        };
        fetchDepartments();
    }, []);

    // Lightweight fetch for error badge counts across ALL departments (no pagination needed, just counts)
    useEffect(() => {
        if (departmentsPerUser.length === 0) return;

        const fetchErrorCounts = async () => {
            try {
                const counts: Record<string, number> = {};

                await Promise.all(
                    baseDepartments.map(async (dept) => {
                        const res = await fetch(
                            `/api/alerts/?department=${encodeURIComponent(dept.name)}&type=ERROR&archive=false&page=1&page_size=1`,
                            { credentials: "same-origin" }
                        );
                        if (!res.ok) return;
                        const data = await res.json();
                        counts[dept.name] = data.count ?? 0;  // DRF returns total count regardless of page_size
                    })
                );

                setErrorCounts(counts);
            } catch (err) {
                console.error("Error fetching error counts:", err);
            }
        };

        fetchErrorCounts();
        const interval = setInterval(fetchErrorCounts, 10000);
        return () => clearInterval(interval);
    }, [departmentsPerUser]);

    // Paginated fetch for selected department's alerts
    const fetchAlertsForDepartment = async (
        department: string,
        archived: boolean,
        pageNumber: number
    ) => {
        if (pageNumber === 1) {
            setIsLoading(true);
            setShowSkeleton(false);
            setHasFetched(false); // ← reset on fresh load

            const skeletonTimer = setTimeout(() => setShowSkeleton(true), 200);

            try {
                const res = await fetch(
                    `/api/alerts/?department=${department}&archive=${archived}&page=${pageNumber}&page_size=21`,
                    { credentials: "same-origin" }
                );
                if (!res.ok) throw new Error("Failed to fetch alerts");

                const data = await res.json();
                const results: Alerts[] = data.results ?? data;

                setAllAlerts(results);
                setHasMore(!!data.next);
            } catch (err) {
                console.error(err);
            } finally {
                clearTimeout(skeletonTimer);
                setIsLoading(false);
                setShowSkeleton(false);
                setHasFetched(true); // ← only now is it safe to show empty state / load more
            }
        } else {
            // ...load more unchanged
        }
    };

    // Trigger fetch when department or archive toggle changes
    useEffect(() => {
        if (!selectedDepartment || departmentsPerUser.length === 0) return;
        setPage(1);
        setHasMore(true);
        setAllAlerts([]);
        setHasFetched(false); // ← reset
        fetchAlertsForDepartment(selectedDepartment, showArchived, 1);
    }, [selectedDepartment, showArchived, departmentsPerUser]);

    const handleLoadMore = () => {
        if (!hasMore || isLoading) return;

        const nextPage = page + 1;
        setPage(nextPage);

        fetchAlertsForDepartment(
            selectedDepartment!,
            showArchived,
            nextPage
        );
    };


    // Handle department card click
    const handleDepartmentClick = (deptName: string) => {
        if (selectedDepartment === deptName) {
            setSelectedDepartment(null);
            setAllAlerts([]);
        } else {
            setSelectedDepartment(deptName);
        }
    };

    // Client-side filters (search, date, type) applied on top of already-fetched dept alerts
    const filteredAlerts = allAlerts.filter(alert => {
        const searchMatch = searchTerm
            ? (alert.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                alert.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                alert.body?.toLowerCase().includes(searchTerm.toLowerCase()))
            : true;

        const alertDate = new Date(alert.created_at);
        const fromDateMatch = fromDate ? alertDate >= new Date(`${fromDate}T00:00:00`) : true;
        const toDateMatch = toDate ? alertDate <= new Date(`${toDate}T23:59:59`) : true;
        const typeMatch = alertType ? alert.type?.toLowerCase() === alertType.toLowerCase() : true;

        return searchMatch && fromDateMatch && toDateMatch && typeMatch;
    });

    function DateInput({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) {
        const [focus, setFocus] = useState(false);

        return (
            <input
                type={focus || value ? "date" : "text"}
                value={value}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                onChange={(e) => onChange(e.target.value)}
                placeholder={label}
                className="date-input"
            />
        );
    }

    // 🔄 Carousel logic for alert-carousel
    const [carouselIndex, setCarouselIndex] = useState(0);

    const calculateVisibleCount = (width: number) => {
        // 🔧 clamp the first breakpoint to avoid Chrome off-by-1 issues
        if (width <= 480) return 1;
        if (width <= 700) return 2;
        if (width <= 900) return 3;
        if (width <= 1100) return 4;
        if (width <= 1300) return 5;
        if (width <= 1500) return 6;
        if (width <= 1650) return 7;
        return departments.length;
    };

    const [visibleCount, setVisibleCount] = useState(calculateVisibleCount(window.innerWidth));
    const [isMobileCarousel, setIsMobileCarousel] = useState(window.innerWidth <= 1650);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            let count = departments.length;
            let mobile = false;

            if (width <= 480) { count = 1; mobile = true; }
            else if (width <= 700) { count = 2; mobile = true; }
            else if (width <= 900) { count = 3; mobile = true; }
            else if (width <= 1100) { count = 4; mobile = true; }
            else if (width <= 1300) { count = 5; mobile = true; }
            else if (width <= 1500) { count = 6; mobile = true; }
            else if (width <= 1650) { count = 7; mobile = true; }

            setVisibleCount(count);
            setIsMobileCarousel(mobile);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [departments.length]);


    const handleNext = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev + 1) % departments.length);
        }
    };

    const handlePrev = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev - 1 + departments.length) % departments.length);
        }
    };

    const getVisibleDepartments = () => {
        if (isMobileCarousel) {
            const visible: typeof departments = [];
            for (let i = 0; i < visibleCount; i++) {
                visible.push(departments[(carouselIndex + i) % departments.length]);
            }
            return visible;
        }
        return departments;
    };

    useEffect(() => {
        let resizeTimer: NodeJS.Timeout;

        const updateCounts = () => {
            const width = window.innerWidth;
            const count = calculateVisibleCount(width);
            setVisibleCount(count);
            setIsMobileCarousel(count < departments.length);
        };

        const handleResize = () => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(updateCounts, 150); // debounce
        };

        updateCounts(); // run once at mount

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimer) clearTimeout(resizeTimer);
        };
    }, [departments.length]);

    // Allowing to archive ERROR alerts

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };


    const toggleArchiveAlert = async (alertId: number, currentlyArchived: boolean) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(`/api/alerts/${alertId}/archive`, {
                method: "PUT",
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify({ archive: !currentlyArchived })
            });

            if (!res.ok) throw new Error("Failed to update alert");

            // Optimistically update local state
            setAllAlerts(prev =>
                prev.filter(a => a.id !== alertId)
            );
        } catch (err) {
            console.error(err);
        }
    };





    return (
        <div className='notification-hub-wrapper'>
            <PortalHeader />
            <div className='alert-side-menu-and-body'>
                <div className='alert-side-menu' />
                <div className='alert-body'>
                    <h1>Alerts</h1>

                    <div className='active-archived-filter'>
                        <button className={showArchived ? "" : "isActive"} onClick={() => setShowArchived(false)}>
                            Active Alerts
                        </button>
                        <button className={showArchived ? "isActive" : ""} onClick={() => setShowArchived(true)}>
                            Archived Alerts
                        </button>
                    </div>

                    {/* Carousel - use handleDepartmentClick instead of inline */}
                    <div className="alert-carousel-track">
                        {isMobileCarousel && (
                            <button className="alert-carousel-arrow alert-arrow-left" onClick={handlePrev}>&lt;</button>
                        )}
                        {getVisibleDepartments().map((dept, idx) => {
                            const count = errorCounts[dept.name] || 0;
                            return (
                                <div key={idx} className="alert-card-wrapper" onClick={() => handleDepartmentClick(dept.name)}>
                                    <div className="alert-card" style={{ backgroundColor: getBackgroundColor(dept.name) }}>
                                        <div className="alert-card-text">
                                            <h2>{dept.name}</h2>
                                            <p>{count} Error Alert{count !== 1 ? "s" : ""}</p>
                                        </div>
                                        {dept.icon}
                                    </div>
                                    {selectedDepartment && selectedDepartment !== dept.name && (
                                        <div className="alert-card-overlay" />
                                    )}
                                </div>
                            );
                        })}
                        {isMobileCarousel && (
                            <button className="alert-carousel-arrow alert-arrow-right" onClick={handleNext}>&gt;</button>
                        )}
                    </div>

                    {/* Filters */}
                    <div className='alert-filters'>
                        <input type="text" className="search-input" placeholder="Search alert"
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <DateInput label="From" value={fromDate} onChange={setFromDate} />
                        <DateInput label="To" value={toDate} onChange={setToDate} />
                        <select className="type-dropdown" value={alertType} onChange={(e) => setAlertType(e.target.value)}>
                            <option value="">All Types</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                        </select>
                    </div>

                    <div className='alert-filters2'>
                        <DateInput label="From" value={fromDate} onChange={setFromDate} />
                        <DateInput label="To" value={toDate} onChange={setToDate} />
                    </div>

                    {/* Alert list */}
                    <div className='alert-list'>
                        {!selectedDepartment && (
                            <p className="alert-placeholder">Select a department to view its alerts.</p>
                        )}

                        {/* Skeleton — initial load only */}
                        {isLoading && showSkeleton && Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="alert-widget">
                                <div className="alert-type-and-title">
                                    <div className="alert-skeleton alert-skeleton-icon" />
                                    <div className="alert-title-and-date" style={{ width: '100%' }}>
                                        <div className="alert-title">
                                            <div className="alert-skeleton alert-skeleton-title" />
                                            <div className="alert-skeleton alert-skeleton-category" />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <div className="alert-skeleton alert-skeleton-date" />
                                            <div className="alert-skeleton alert-skeleton-time" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="alert-skeleton alert-skeleton-body" />
                                    <div className="alert-skeleton alert-skeleton-body short" />
                                </div>
                            </div>
                        ))}

                        {/* Empty state */}
                        {hasFetched && selectedDepartment && filteredAlerts.length === 0 && (
                            <p className="alert-placeholder">No alerts found for this department.</p>
                        )}

                        {/* Real alerts — always visible, even while loading more */}
                        {hasFetched && filteredAlerts.map(alert => (
                            <div key={alert.id} className='alert-widget'>
                                <div className='alert-type-and-title'>
                                    <div className={`alert-type-icon ${getAlertTypeClass(alert.type?.toLowerCase())}`}>
                                        {alert.type?.toLowerCase() === "info" && <IoInformationCircle className="alert-icon2" />}
                                        {alert.type?.toLowerCase() === "warning" && <TiWarning className="alert-icon2" />}
                                        {alert.type?.toLowerCase() === "error" && <MdOutlineError className="alert-icon2" />}
                                    </div>
                                    <div className='alert-title-and-date'>
                                        <div className='alert-title'>
                                            <h2>{alert.title}</h2>
                                            <p>Category: {alert.category}</p>
                                        </div>
                                        <div className='alert-date'>
                                            <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                                            <span className="alert-time">
                                                {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                            {alert.type?.toLowerCase() === "error" && (
                                                <button className='archive-button' onClick={() => toggleArchiveAlert(alert.id, alert.archive)}>
                                                    <BiSolidArchive /> {alert.archive ? "Unarchive" : "Archive"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <p>{alert.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Load More — skeletons append below existing cards */}
                    {hasFetched && !isLoading && hasMore && selectedDepartment && (
                        <div className="load-more-wrapper">
                            <button
                                className="load-more-button confirm-btn"
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? "Loading..." : "Load More"}
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};



export default AlertHub;

